import React, { useState } from 'react';
import { authService } from '../../services/apiService';
import { toast } from 'react-toastify';
import { useT } from '../../utils/i18n';

export const Settings = () => {
  const t = useT();
  const [apiConfig, setApiConfig] = useState({
    provider: 'claude',
    apiKey: '',
    model: 'claude-sonnet-4-6',
    maxTokens: 2000,
    temperature: 0.7,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);

  const handleConfigChange = (e) => {
    const { name, value } = e.target;
    setApiConfig((prev) => ({
      ...prev,
      [name]: name === 'maxTokens' || name === 'temperature' ? parseFloat(value) : value,
    }));
  };

  const handleSaveConfig = async () => {
    if (!apiConfig.apiKey.trim()) {
      toast.error(t('legacySettings.apiKeyRequired'));
      return;
    }

    setIsLoading(true);
    try {
      await authService.configureAPI(apiConfig);
      toast.success(t('legacySettings.saved'));
    } catch (error) {
      toast.error(error.response?.data?.message || t('legacySettings.failedSave'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      {/* API Configuration */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">{t('legacySettings.title')}</h2>

        <div className="space-y-4">
          {/* Provider */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('legacySettings.provider')}
            </label>
            <select
              name="provider"
              value={apiConfig.provider}
              onChange={(e) => {
                const provider = e.target.value;
                const defaultModel = provider === 'claude' ? 'claude-sonnet-4-6' : provider === 'openai' ? 'gpt-4' : '';
                setApiConfig((prev) => ({ ...prev, provider, model: defaultModel }));
              }}
              className="input"
            >
              <option value="claude">Claude (Anthropic)</option>
              <option value="openai">OpenAI</option>
            </select>
          </div>

          {/* API Key */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('legacySettings.apiKey')}
            </label>
            <div className="relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                name="apiKey"
                value={apiConfig.apiKey}
                onChange={handleConfigChange}
                placeholder="sk-..."
                className="input pr-10"
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showApiKey ? '✕' : '◉'}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {t('legacySettings.apiKeySecure')}
            </p>
          </div>

          {/* Model */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('legacySettings.model')}
            </label>
            <select name="model" value={apiConfig.model} onChange={handleConfigChange} className="input">
              {apiConfig.provider === 'claude' ? (
                <>
                  <option value="claude-sonnet-4-6">Claude Sonnet 4.6 (Recommended)</option>
                  <option value="claude-opus-4-7">Claude Opus 4.7</option>
                  <option value="claude-haiku-4-5-20251001">Claude Haiku 4.5</option>
                </>
              ) : (
                <>
                  <option value="gpt-4">GPT-4</option>
                  <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                </>
              )}
            </select>
          </div>

          {/* Temperature */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('legacySettings.temperature', { value: apiConfig.temperature })}
            </label>
            <input
              type="range"
              name="temperature"
              min="0"
              max="2"
              step="0.1"
              value={apiConfig.temperature}
              onChange={handleConfigChange}
              className="w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('legacySettings.temperatureNote')}
            </p>
          </div>

          {/* Max Tokens */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('legacySettings.maxTokens')}
            </label>
            <input
              type="number"
              name="maxTokens"
              value={apiConfig.maxTokens}
              onChange={handleConfigChange}
              className="input"
              min="100"
              max="4000"
            />
          </div>

          <button
            onClick={handleSaveConfig}
            disabled={isLoading}
            className="btn btn-primary w-full"
          >
            {isLoading ? t('legacySettings.saving') : t('legacySettings.save')}
          </button>
        </div>
      </div>

      {/* Editorial Rules Section */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">{t('legacySettings.editorialTitle')}</h2>
        <p className="text-gray-600">
          {t('legacySettings.editorialDesc')}
        </p>
      </div>
    </div>
  );
};
